// ─────────────────────────────────────────────────────────────────────────
// duplicateRecord — the client half of the staged duplicate check.
//
// The backend (POST /v1/module/duplicate-check) decides WHETHER a record is a
// duplicate, entirely from the module's configured stages. This module decides
// only how to SAY it, and it takes every word from config too:
//
//   automations.duplicateCheck.message  → the sentence, with {tokens}
//   automations.duplicateCheck.policy   → "prompt" (offer to update) | "block"
//   automations.duplicateCheck.displayFields → which keys name the record
//
// Nothing here knows a module, a field, or how long "recently" is.
// ─────────────────────────────────────────────────────────────────────────

/**
 * describeAge — "how stale is the existing record?", in words.
 *
 * The requirement asks for exactly this: "in the error message we need to show
 * last updated date — for example 60 days back the same candidate is updated,
 * like that — so we can get a proper idea that the candidate already was there
 * with the date."
 *
 * A bare date ("07 Aug 2026") makes the reader do the arithmetic; the elapsed
 * time is the part that carries the decision, so both are offered and the
 * caller's template chooses.
 */
export function describeAge(daysAgo) {
  // null/undefined/'' must NOT fall through to Number()'s 0, which would render
  // a record with no timestamp at all as "updated today" — a confident false
  // statement about exactly the fact the user is being asked to judge.
  if (daysAgo === null || daysAgo === undefined || daysAgo === '') return '';
  const days = Number(daysAgo);
  if (!Number.isFinite(days) || days < 0) return '';
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  if (days < 60) return 'about a month ago';
  if (days < 365) return `about ${Math.round(days / 30)} months ago`;
  const years = Math.floor(days / 365);
  return years === 1 ? 'over a year ago' : `over ${years} years ago`;
}

/**
 * describeRecord — a human label for the matched record, built from whichever
 * of the configured display fields actually carry a value.
 */
export function describeRecord(record, displayFields) {
  const keys = Array.isArray(displayFields) && displayFields.length
    ? displayFields
    : Object.keys(record ?? {});
  const parts = keys
    .map((key) => record?.[key])
    .map((v) => (v === null || v === undefined ? '' : String(v).trim()))
    .filter(Boolean);
  return parts.join(' · ');
}

/**
 * fillTemplate — {token} substitution. An unknown token is left INTACT so a
 * typo in admin config is visible rather than silently blanking the sentence.
 */
export function fillTemplate(template, tokens = {}) {
  if (!template) return '';
  return String(template).replace(
    /\{(\w+)\}/g,
    (match, key) => (tokens[key] === undefined ? match : String(tokens[key])),
  );
}

/**
 * duplicateTokens — everything a configured message may reference.
 */
export function duplicateTokens(result, displayFields) {
  const name = describeRecord(result?.record, displayFields);
  const age = describeAge(result?.updatedDaysAgo);
  return {
    name: name || 'an existing record',
    age: age || 'previously',
    days: Number.isFinite(Number(result?.updatedDaysAgo)) ? String(result.updatedDaysAgo) : '',
    updatedOn: result?.updatedAt ? String(result.updatedAt).slice(0, 10) : '',
    stage: result?.stage ?? '',
    weight: result?.weight ?? '',
  };
}

// Default wording, used until an admin writes their own. Deliberately states
// BOTH the identity and the age, because those are the two things that let
// someone decide whether this really is the same person.
export const DEFAULT_DUPLICATE_MESSAGE =
  'We already have {name} on file \u2014 last updated {age}. '
  + 'You can carry on and update that record with what you\u2019ve just entered, '
  + 'or go back and check first.';

export const DEFAULT_BLOCK_MESSAGE =
  'We already have {name} on file \u2014 last updated {age}. '
  + 'Please open that record and update it, rather than adding a second copy.';

/**
 * duplicateMessage — the finished sentence for a result.
 */
export function duplicateMessage(result, cfg = {}) {
  const tokens = duplicateTokens(result, cfg.displayFields);
  const isBlock = (result?.policy ?? cfg.policy) === 'block';
  const template = result?.message
    || cfg.message
    || (isBlock ? DEFAULT_BLOCK_MESSAGE : DEFAULT_DUPLICATE_MESSAGE);
  return fillTemplate(template, tokens);
}

/**
 * duplicateBlocks — should the create be refused outright?
 *
 * The response's own policy wins (it comes from the module config the backend
 * actually applied); the caller's config is only a fallback for a backend that
 * predates the field. Default is "prompt", so a misconfiguration inconveniences
 * rather than blocks.
 */
export function duplicateBlocks(result, cfg = {}) {
  if (!result?.duplicate) return false;
  return (result.policy ?? cfg.policy ?? 'prompt') === 'block';
}

/**
 * duplicateFacts — the matched record as scannable label/value pairs.
 *
 * The prose sentence carries the same information, but a reader deciding "is
 * this the same person?" should not have to parse it out of a paragraph. The
 * facts that settle it are WHO and HOW STALE, so those are surfaced; whatever
 * else the module chose to display comes with them.
 *
 * Labels are derived from the key itself ("firstName" → "First name"), so a
 * module that adds a display field gets a sensible label with no extra config.
 */
export function duplicateFacts(result, displayFields) {
  const record = result?.record ?? {};
  const keys = Array.isArray(displayFields) && displayFields.length
    ? displayFields
    : Object.keys(record);

  const facts = keys
    .map((key) => ({ label: humanizeKey(key), value: record[key] }))
    .filter((f) => f.label && f.value !== undefined && f.value !== null && String(f.value).trim() !== '');

  const age = describeAge(result?.updatedDaysAgo);
  if (age) facts.push({ label: 'Last updated', value: age });
  return facts;
}

// humanizeKey — "firstName" / "contact_number" → "First name".
// Only the first word is capitalised: these are labels in a list, not titles.
function humanizeKey(key) {
  const words = String(key ?? '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}
